import { Box, Button, Link } from "@material-ui/core";
import ProgressIndicator from "./ImportView/ImportFileSection/ProgressIndicator";
import {
  ImportFileContent,
  SaveProgressText,
} from "./ImportView/ImportFileSection/styled";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { BulkSaveResult } from "pages/Accounts/accountContext.types";
import { CreateAccountBody } from "@dashboard-v3/api/src/types/accounts";
import { BulkResult, useAccountState } from "pages/Accounts/accountContext";
import { Action, State } from "./reducer";

type ProgressState = {
  percentage: number;
  processed: number;
  total: number;
  accountsWithError: BulkSaveResult["errors"];
  succeeded: string[];
};

interface Props {
  state: State;
  dispatch: React.Dispatch<Action>;
  closeModal: () => void;
}

export default function SavingAccountsView({
  state,
  dispatch,
  closeModal,
}: Props) {
  const { t } = useTranslation("accounts");
  const { actions } = useAccountState();
  const completedRef = useRef(false);
  const [progress, setProgress] = useState<ProgressState>(initProgress);

  useEffect(() => {
    if (state.saveResults) {
      // Only show the results
      setProgress({
        percentage: 100,
        processed: state.saveResults.accountsDone,
        total: state.saveResults.total,
        accountsWithError: state.saveResults.errors,
        succeeded: state.saveResults.succeeded,
      });
    } else {
      // Start saving accounts
      onSave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (completedRef.current) {
      dispatch({
        type: "SAVE_ACCOUNTS_FINISHED",
        results: {
          accountsDone: progress.processed,
          total: progress.total,
          errors: progress.accountsWithError,
          succeeded: progress.succeeded,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedRef.current]);

  const isSaveFinished = completedRef.current || progress.percentage === 100;
  const finishedWithErrors =
    isSaveFinished && !!progress.accountsWithError.length;

  const getResultsText = () => {
    const { accountsWithError, processed, total } = progress;
    const { length: errorCount } = accountsWithError;
    const translationProps = { processed, total, errorCount };

    if (!isSaveFinished) {
      const key = { UPSERT: "saving", UPDATE: "update", DELETE: "delete" }[
        state.action
      ];
      return t(`importAccountsModal.import.${key}`, {
        processed,
        total,
        count: total,
      });
    }

    if (state.action === "DELETE") {
      return t(
        accountsWithError.length
          ? "modal.deleteFinishedWithErrors"
          : "modal.deleteFinished",
        { ...translationProps, count: total }
      );
    }

    if (state.action === "UPSERT") {
      if (accountsWithError.length) {
        return t("importAccountsModal.import.finishedWithErrors", {
          ...translationProps,
          count: total,
        });
      }

      return t("importAccountsModal.import.saveFinished", {
        processed,
        count: total,
      });
    }

    if (accountsWithError.length) {
      return t("modal.updateFinishedWithErrors", {
        ...translationProps,
        count: total,
      });
    }

    return t("modal.import.updateFinished", { processed, count: processed });
  };

  const handleSaveAction = () => {
    if (!completedRef.current) {
      completedRef.current = true;
    } else {
      setProgress(initProgress());
      // setStatus("IDLE");
    }
  };

  const onSave = async () => {
    completedRef.current = false;
    const toSave =
      state.action === "DELETE"
        ? state.emailsToDelete.map(email => ({ email } as CreateAccountBody))
        : state.loadedFiles.flatMap(file => file.items).map(i => i.account);
    const accountsToSave = chunkArray(toSave, 100);

    setProgress(prev => ({
      ...prev,
      total: toSave.length,
    }));

    // TODO: split upload to pack of 1000
    for (const [index, accounts] of accountsToSave.entries()) {
      if (completedRef.current) break;

      let result: BulkResult;
      if (state.action === "UPDATE") {
        result = await actions.bulkUpdate(state.selectedColumns, accounts);
      } else if (state.action === "DELETE") {
        result = await actions.bulkDelete(accounts.map(a => a.email));
      } else {
        result = await actions.bulkUpsert(accounts);
      }

      const { accountsDone, errors } = result;
      const failed = new Set(errors.map(e => e.email));
      const succeeded = accounts
        .map(a => a.email)
        .filter(email => !failed.has(email));

      setProgress(prev => ({
        ...prev,
        processed: prev.processed + accountsDone,
        percentage: getProgressPercentage(index, accountsToSave),
        accountsWithError: [...prev.accountsWithError, ...errors],
        succeeded: [...prev.succeeded, ...succeeded],
      }));
    }

    completedRef.current = true;
    actions.synchronizeAccounts();
    actions.reloadAccounts();
  };

  function onShowErrors() {
    dispatch({ type: "SHOW_SAVED_RESULTS" });
  }

  return (
    <Box p="2rem">
      <ImportFileContent>
        <ProgressIndicator
          value={progress.percentage}
          finishedWithErrors={finishedWithErrors}
          withPercentage
        />
        <SaveProgressText variant="caption" isError={finishedWithErrors}>
          {getResultsText()}
          {(finishedWithErrors ||
            (isSaveFinished && state.action === "DELETE")) && (
            <Link onClick={onShowErrors}>
              {t("importAccountsModal.import.resultsLink")}
            </Link>
          )}
        </SaveProgressText>
        {!isSaveFinished && (
          <Button variant="outlined" size="small" onClick={handleSaveAction}>
            {t("importAccountsModal.import.abort")}
          </Button>
        )}
        {isSaveFinished && (
          <Button variant="outlined" size="small" onClick={closeModal}>
            Close
          </Button>
        )}
      </ImportFileContent>
    </Box>
  );
}

const initProgress = () => ({
  percentage: 0,
  processed: 0,
  total: 0,
  accountsWithError: [],
  succeeded: [],
});

function chunkArray(array: Array<any>, chunkSize: number) {
  const chunks = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
}

function getProgressPercentage(index: number, arr: any[]) {
  const percentage = ((index + 1) / arr.length) * 100;
  return Math.floor(percentage);
}
